'use client';

/* eslint-disable @next/next/no-img-element */
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { BRAND, NAV_LINKS } from '@/content/site';
import { useT } from '@/i18n/client';
import { getLearnerToken } from '@/lib/learnerSession';
import { CTAButton } from '../ui/CTAButton';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';

export function Navbar() {
  const t = useT();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  // Close the mobile menu on navigation.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Reflect learner session state (re-check on each navigation).
  useEffect(() => {
    setSignedIn(Boolean(getLearnerToken()));
  }, [pathname]);

  const accountCta = signedIn
    ? { href: '/account', label: t('nav.myAccount') }
    : { href: '/account/register', label: t('nav.getStarted') };

  /** Translated nav label, falling back to the authored label. */
  const navLabel = (link: { href: string; label: string }): string => {
    const translated = t(`nav.links.${link.href}`);
    return translated === `nav.links.${link.href}` ? link.label : translated;
  };

  return (
    <nav className="vn-navbar" aria-label="Main navigation">
      <div className="vn-container vn-navbar__inner">
        <Link href="/" className="vn-navbar__brand" aria-label={`${BRAND.name} home`}>
          <img src="/logo.png" alt="" width={48} height={48} />
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-md)', color: 'var(--vn-navy)', fontWeight: 600 }}>
            {BRAND.wordmark}
          </span>
        </Link>

        <button
          type="button"
          className="vn-navbar__toggle"
          aria-expanded={open}
          aria-controls="main-menu"
          onClick={() => setOpen((value) => !value)}
        >
          <span aria-hidden="true" style={{ fontSize: '1.5rem' }}>
            {open ? '✕' : '☰'}
          </span>
          <span className="vn-sr-only">{open ? 'Close menu' : 'Open menu'}</span>
        </button>

        <ul id="main-menu" className={`vn-navbar__links${open ? ' is-open' : ''}`}>
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <Link
                href={link.href}
                className="vn-navbar__link"
                aria-current={pathname?.startsWith(link.href) ? 'page' : undefined}
              >
                {navLabel(link)}
              </Link>
            </li>
          ))}
          <li className="vn-navbar__mobile-cta">
            <ThemeToggle />
            <LanguageSwitcher />
            <CTAButton href={accountCta.href} small trackLabel="nav-account-cta">
              {accountCta.label}
            </CTAButton>
          </li>
        </ul>

        <div className="vn-navbar__cta">
          <ThemeToggle />
          <LanguageSwitcher />
          <CTAButton href={accountCta.href} small trackLabel="nav-account-cta">
            {accountCta.label}
          </CTAButton>
        </div>
      </div>
    </nav>
  );
}
